import { z } from 'zod';

// Fallback ONLY. The live options are sourced from banner_severity's
// validation_rules.in (Decision D3); this constant covers the pre-load / missing-option
// case so a future seeder widening the enum needs no code change.
export const BANNER_SEVERITY_FALLBACK = ['info', 'warning', 'critical'] as const;

// The three banner fields. Severity is a free string at the object level (the allowed set is
// dynamic — sourced from the catalogue); the "must be a KNOWN severity" rule is enforced by
// the factory's superRefine against the live allowed list, so an enabled banner with a
// stale/narrowed/invalid severity fails at the field rather than only at the backend Rule::in.
const bannerRulesObject = z.object({
    banner_enabled: z.boolean(),
    banner_severity: z.string(),
    banner_text: z.string().max(1000, 'Max 1000 characters'),
});

export type BannerRulesFormValues = z.infer<typeof bannerRulesObject>;

/**
 * Build the banner-rules schema for a given allowed-severity set. The panel threads the live
 * options (banner_severity's validation_rules.in, with BANNER_SEVERITY_FALLBACK) through here
 * so an enabled banner requires severity to be one of the CURRENT allowed values. Severity is
 * only enforced when the banner is enabled — disabling relaxes both fields (they are hidden).
 */
export const makeBannerRulesSchema = (allowedSeverities: readonly string[]) => {
    const allowed = new Set(allowedSeverities);
    return bannerRulesObject.superRefine((v, ctx) => {
        if (!v.banner_enabled) return;
        if (v.banner_severity.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['banner_severity'],
                message: 'Select a severity',
            });
            return;
        }
        if (!allowed.has(v.banner_severity)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['banner_severity'],
                message: 'Select a valid severity',
            });
        }
    });
};

// Fallback schema for the pre-load / missing-catalogue case. The panel rebuilds the schema
// from the live allowed set once the catalogue loads (see CampaignBannerPanel).
export const bannerRulesSchema = makeBannerRulesSchema(BANNER_SEVERITY_FALLBACK);
